import React, { Fragment, useRef, useEffect, useState } from 'react'
import { NavLink, Link } from 'react-router-dom'
import clsx from 'clsx'
import { makeStyles, useTheme, createStyles } from '@material-ui/core/styles'
import { SvgIcon } from '@material-ui/core'
import type { DrawerProps } from '@material-ui/core/Drawer'
import Drawer from '@material-ui/core/Drawer'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import type { NavItem } from '@homeviews/models'
import { Typography, LinearProgress } from '@homeviews/shared-ui'
import type { ITheme } from '@homeviews/styles'
import { formatRating } from '@homeviews/utils'
import { mixpanelTrack, EVENTS } from '@homeviews/seo'
import { IS_COMPANY_GROUP_ADMIN } from '@homeviews/users'
import {
  logout,
  useSignOutMutation,
  useGetCompanyStatusQuery,
  getUser,
  useGetAwardsStatsQuery,
  useGetDashboardDevelopmentsQuery,
} from '@homeviews/store'

const drawerWidth = 250
const CURRENT_YEAR = new Date().getFullYear()

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
      backgroundColor: theme.colors.brandDarkGrey,
      flex: 1,
      [theme.breakpoints.up('md')]: {
        height: '100%',
        transform: 'translateX(0)!important',
        visibility: 'visible!important',
        position: 'relative',
      },
    },
    innerWrapper: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: '100%',
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 2.5),
      height: theme.spacing(8.75),
      minHeight: theme.spacing(8.75),
      maxHeight: theme.spacing(8.75),
      // necessary for content to be below app bar
      // ...theme.mixins.toolbar
    },
    navWrapper: {},
    navItem: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 2.5),
      textDecoration: `none !important`,
      '&:hover': {
        cursor: 'pointer',
        '&:before': {
          transform: 'translateX(0)',
        },
        '& p': {
          color: theme.colors.white,
        },
      },
      '&.activeMenuLink p': {
        color: theme.colors.white,
      },
      '&:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        width: 3,
        backgroundColor: theme.colors.primary,
        transition: 'transform .3s ease-in-out',
        transform: 'translateX(-3px)',
      },
      '&.activeMenuLink:before': {
        transform: 'translateX(0)',
      },
      '& img': {},
      '& p': {
        color: 'rgba(255,255,255,0.6)',
        transition: 'color .3s ease-in-out',
      },
    },
    navItemIconWrapper: {
      width: '33px',
    },
    logoWrapper: {
      '& img': {
        width: '144px',
      },
    },
    navGroupTitle: {
      margin: theme.spacing(2, 2.5, 1, 2.5),
      color: 'rgba(255,255,255,0.3)',
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.8px',
      lineHeight: '14px',
      textTransform: 'uppercase',
    },
    bottomNav: {
      marginTop: 'auto',
    },
    bottomNavInfo: {
      borderTop: '1px solid rgba(255,255,255,0.1)',
      padding: theme.spacing(2.875, 0),
      margin: theme.spacing(1, 2.5),
      marginTop: theme.spacing(2),
      '& p': {
        color: 'rgba(255,255,255,0.6)',
        marginBottom: 5,
      },
    },
    navItemSummary: {
      flexWrap: 'wrap',
    },
    summary: {
      width: '100%',
      '& .MuiLinearProgress-root': {
        backgroundColor: 'rgba(255,255,255,0.14)!important',
      },
    },
    progressBar: {
      marginTop: 7,
    },
    getStartedText: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        stroke: theme.colors.primary,
        fontSize: 19,
        marginLeft: 3,
      },
    },
  }),
)

interface IProps extends DrawerProps {
  open: boolean
  menuItems: NavItem[]
  hideOnboardingProgress?: boolean
}

export const Sidebar: React.FC<IProps> = ({
  menuItems,
  open = false,
  hideOnboardingProgress = false,
  ...rest
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const [signOut] = useSignOutMutation()
  const history = useHistory()
  const navGroupRef = useRef('')
  const wrapperRef = useRef(null)
  const user = useSelector(getUser)
  const isCompanyGroupAdmin = IS_COMPANY_GROUP_ADMIN(user)
  const [sidebarHeight, setSidebarHeight] = useState(undefined)
  const [excludedNavItems, setExcludedNavItems] = useState<any[]>([])
  const [companyProgress, setCompanyProgress] = useState(0)

  const {
    data: companyProgressData,
    error,
    isFetching,
  } = useGetCompanyStatusQuery(user?.developer_id, {
    skip: !user?.developer_id || hideOnboardingProgress,
  })

  const { data: awardsData } = useGetAwardsStatsQuery({
    skip: !user?.developer_id,
  })

  const { data: developmentsData } = useGetDashboardDevelopmentsQuery({
    skip: !user?.id,
  })

  useEffect(() => {
    if (companyProgressData?.data?.overall_setup_progress) {
      setCompanyProgress(
        companyProgressData?.data?.overall_setup_progress * 100,
      )
    }
  }, [companyProgressData])

  useEffect(() => {
    const newHeight = wrapperRef?.current?.scrollHeight

    if (newHeight) {
      setSidebarHeight(newHeight)
    }
  }, [])

  useEffect(() => {
    if (awardsData?.data?.length === 0) {
      const newItems = [...excludedNavItems]
      newItems.push('/awards')
      setExcludedNavItems(newItems)
    }
  }, [awardsData?.data])

  useEffect(() => {
    if (developmentsData?.length === 0) {
      const newItems = [...excludedNavItems]
      newItems.push('/dashboard')
      setExcludedNavItems(newItems)
    }
  }, [developmentsData])

  useEffect(() => {
    if (!user?.developer_id) {
      return
    }

    mixpanelTrack('WEB_SESSION', {
      Name: user?.name || '',
      Email: user?.email || '',
    })
  }, [user?.id])

  const handleLogoutClick = async () => {
    mixpanelTrack('LOGOUT')

    // Best-effort: audit the sign-out and revoke the token server-side while the
    // bearer is still present. The request must be started before we clear the
    // local session (logout() drops the token that prepareHeaders reads), but it
    // must never delay the redirect, so cap the wait and proceed regardless.
    const SIGN_OUT_TIMEOUT_MS = 1500
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, SIGN_OUT_TIMEOUT_MS)
    })
    try {
      await Promise.race([signOut().unwrap(), timeout])
    } catch {
      // Ignore: the user is signing out regardless of the backend response.
    } finally {
      clearTimeout(timeoutId)
    }

    dispatch(logout())
    history.push('/login')
  }

  function handleNavClick(name: string) {
    mixpanelTrack(name)
  }

  function handleExternalNavClick(name: string) {
    mixpanelTrack(name)
  }

  return (
    <Drawer
      className={classes.drawer}
      anchor="left"
      open={open}
      classes={{
        paper: classes.drawerPaper,
      }}
      {...rest}
    >
      <div
        ref={wrapperRef}
        className={classes.innerWrapper}
        style={{
          height: `${sidebarHeight}px`,
        }}
      >
        <div className={classes.drawerHeader}>
          <Link to="/" className={classes.logoWrapper}>
            <img
              src="assets/images/homeviews-logo-light.svg"
              alt="HomeViews logo sidebar"
            />
          </Link>
        </div>
        <div className={classes.navWrapper}>
          {user && !isCompanyGroupAdmin && !hideOnboardingProgress && (
            <NavLink
              to="/"
              className={clsx(classes.navItem, classes.navItemSummary)}
              activeClassName="activeMenuLink"
              exact={true}
              onClick={() => handleNavClick('VIEW_GET_STARTED')}
            >
              <Typography className={classes.getStartedText}>
                Get Started{' '}
                <SvgIcon>
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </SvgIcon>
              </Typography>
              <div className={classes.summary}>
                <Typography>
                  Optimal setup {formatRating(companyProgress)}% complete
                </Typography>
                <LinearProgress
                  value={companyProgress}
                  className={classes.progressBar}
                  barColor="#00DEB6"
                  variant="rounded"
                />
              </div>
            </NavLink>
          )}
          {!!menuItems &&
            menuItems.map((item: NavItem) => {
              if (excludedNavItems.includes(item?.slug)) {
                return false
              }

              let groupTitle = null
              if (item.group !== navGroupRef?.current) {
                navGroupRef.current = item.group
                groupTitle = (
                  <Typography className={classes.navGroupTitle}>
                    {item.group}
                  </Typography>
                )
              }
              return (
                <Fragment key={item.slug || item.url}>
                  {groupTitle && groupTitle}
                  {item.external ? (
                    <a
                      href={item.url}
                      className={classes.navItem}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        handleExternalNavClick(item.seoName || item.title)
                      }
                    >
                      <div className={classes.navItemIconWrapper}>
                        <img
                          src={`assets/icons/nav/icons-tiny-${item.icon}`}
                          alt={`${item.title} icon`}
                        />
                      </div>
                      <Typography>{item.title}</Typography>
                    </a>
                  ) : (
                    <NavLink
                      to={item.slug || { pathname: item.url }}
                      className={classes.navItem}
                      activeClassName="activeMenuLink"
                      exact={item.slug === '/' ? true : false}
                      onClick={() => handleNavClick(item.eventName)}
                    >
                      <div className={classes.navItemIconWrapper}>
                        <img
                          src={`assets/icons/nav/icons-tiny-${item.icon}`}
                          alt={`${item.title} icon`}
                        />
                      </div>
                      <Typography>{item.title}</Typography>
                    </NavLink>
                  )}
                </Fragment>
              )
            })}
        </div>
        <div className={classes.bottomNav}>
          <div className={classes.navItem} onClick={handleLogoutClick}>
            <div className={classes.navItemIconWrapper}>
              <img src={`assets/icons/medium/exit.svg`} alt={`Logout icon`} />
            </div>
            <Typography>Logout</Typography>
          </div>
        </div>
        <div className={classes.bottomNavInfo}>
          <Typography>© 2018-{CURRENT_YEAR} HomeViews</Typography>
          <Typography>All rights reserved</Typography>
        </div>
      </div>
    </Drawer>
  )
}
